import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Observable, of, throwError } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { ValueHelperInterface } from '../../interfaces/value-helper.interface';
import { StatusModel } from '../../models/low-level/status.model';
import { ValueHelperModel } from '../../models/low-level/value-helper.model';
import { TextModel } from '../../models/primitive/text.model';
import { selectConfigEntries } from '../../state/config/selectors/config.selector';
import { LoadStatus } from '../../state/load-status';
import { initValueHelpers } from '../../state/value-helper/actions/value-helper.actions';
import {
    selectBillableCodes,
    selectCodeListForId,
    selectCustomerSignatureAgreementTextClause,
    selectDangerousGoodWarning,
    selectEquipmentErrorCode,
    selectModalityValue,
    selectNotificationTypeLabel,
    selectOsrLimitValueHelper,
    selectParticipantTitles,
    selectPdaConfigValues,
    selectPhoneFixIndicator,
    selectQualityControlQuestions,
    selectReasonsMissingProtocolsByCode,
    selectRemoteWorkTimeRecordingTaskTypeInfo,
    selectTimeRecordingBillableCodes,
    selectTravelOfficeStart,
    selectValueHelperLoaded,
    selectValueHelpersById,
    selectValueHelperStatus,
} from '../../state/value-helper/selectors/value-helper.selector';
import { FormParticipantTitles } from '../../types/form-participant-titles';
import { EventService } from '../event/event.service';
import { ValueHelperState } from '../../state/value-helper';

/**
 * NgRx-backed implementation of {@link ValueHelperInterface}. The value-help reference data
 * lives in the value-help store slice; every getter reads it through selectors instead of an
 * in-memory array.
 *
 * **Config coupling:** the FOC / customer-damage getters read the config flags from the
 * config slice (via the value-help selectors that compose `selectIsFocReplacementEnabled` /
 * `selectIsCustomerDamageEnabled`). That slice is only populated when the config NgRx service
 * is active, which `enableNgRx` guarantees by serving both from their slices together; with config
 * off those flags read their defaults (`false`). See the migration plan for the accepted
 * trade-off.
 */
@Injectable({
    providedIn: 'root',
})
export class ValueHelperService implements ValueHelperInterface, InitializableInterface {
    public get isInitialized(): boolean {
        return this.store.selectSignal(selectValueHelperLoaded)();
    }

    public constructor(
        private readonly eventService: EventService,
        private readonly store: Store,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {
        this.eventService.syncReceiveCompleted$.subscribe(() => this.init());
    }

    /**
     * The FOC additional labor/material code lists, keyed by value-help ID, when FOC
     * replacement is enabled. Backed by a selector so it is always consistent — no longer
     * lazily populated as a side effect of the billable getters.
     */
    public get codeListForId(): Record<string, string[]> {
        return this.store.selectSignal(selectCodeListForId)();
    }

    /**
     * Trigger the load effect and resolve once the slice settles: complete on success, or error
     * on failure. Rehydration from the offline cache is also triggered by the dispatched action,
     * so cached value helpers are available while the backend request is in flight.
     */
    public init(): Observable<void> {
        this.store.dispatch(initValueHelpers());
        return this.store.select(selectValueHelperStatus).pipe(
            filter((status) => status === LoadStatus.Loaded || status === LoadStatus.Error),
            take(1),
            switchMap((status) =>
                status === LoadStatus.Error
                    ? throwError(() => new Error('ValueHelperService: init failed'))
                    : of(undefined as void),
            ),
        );
    }

    private checkIfServiceIsInitialized(methodName: string): void {
        if (!this.isInitialized) {
            throw new Error(`${methodName}: Trying to call ValueHelperService before initialization!`);
        }
    }

    /**
     * Warn once if a config-dependent getter is used before the config slice is populated —
     * the FOC / customer-damage flags would silently read their defaults. See the class doc.
     *
     * @param methodName the calling getter, for the log message
     */
    private warnIfConfigNotLoaded(methodName: string): void {
        if (Object.keys(this.store.selectSignal(selectConfigEntries)()).length === 0) {
            this.log.warn(
                `ValueHelperService: ${methodName}: config slice is empty; FOC/customer-damage flags default to false. Is enableNgRx on?`,
            );
        }
    }

    /**
     * Wrap the plain value helpers returned by the selectors into `ValueHelperModel` instances,
     * preserving the class-based return contract the facade exposes to its consumers.
     *
     * @param valueHelpers the plain value helpers from the store
     * @returns the value helpers as model instances
     */
    private toValueHelperModels(valueHelpers: ValueHelperState[]): ValueHelperModel[] {
        return valueHelpers.map((valueHelper) => ValueHelperModel.fromState(valueHelper));
    }

    public getReasonsMissingProtocolsByCode(codeLevel: string): ValueHelperModel[] {
        return this.toValueHelperModels(this.store.selectSignal(selectReasonsMissingProtocolsByCode(codeLevel))());
    }

    public getNotificationTypeLabel(notificationType: ConfigConstants.NotificationTypes): string {
        this.checkIfServiceIsInitialized(`getNotificationTypeLabel`);
        return this.store.selectSignal(selectNotificationTypeLabel(notificationType))();
    }

    public getEquipmentBKInfo(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getEquipmentBKInfo`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDEquipmentBKInfo))(),
        );
    }

    public getCommercialInfoDefaultCode(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getCommercialInfoDefaultCode`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDCommercialInfoDefaultCode))(),
        );
    }

    public getNotificationEffectCode(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getNotificationEffectCode`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDNotificationEffectCode))(),
        );
    }

    public getEquipmentErrorCode(codeLevel: string): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getEquipmentErrorCode`);
        return this.toValueHelperModels(this.store.selectSignal(selectEquipmentErrorCode(codeLevel))());
    }

    public getEquipmentReason(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getEquipmentReason`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDEquipmentReason))(),
        );
    }

    public getEquipmentStatusAfterFix(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getEquipmentStatusAfterFix`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDEquipmentStatusAfterFix))(),
        );
    }

    public getEquipmentStatusInArrival(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getEquipmentStatusInArrival`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDEquipmentStatusInArrival))(),
        );
    }

    public getCentriCareStatusAfterFix(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getCentriCareStatusAfterFix`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDCentriCareStatusAfterFix))(),
        );
    }

    public getCentriCareStatInArrival(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getCentriCareStatInArrival`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDCentriCareStatInArrival))(),
        );
    }

    public getExpenseType(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getExpenseType`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDExpenseType))(),
        );
    }

    /**
     * Wrap value helpers as `DisplayDecorator<TextModel>`s for the dropdown-style getters. The
     * visible label comes from `labelField` (`descText` by default, `codeLevel` for PHI data
     * codes); the value is always `code`. Kept in the facade — not the selectors — because the
     * DisplayDecorator / TextModel view-model layer belongs to the OData architecture that the
     * store must not depend on and that will be dropped once the facade is dissolved.
     *
     * @param valueHelpers the value helpers to wrap
     * @param labelField the field to use as the display label
     * @returns the decorated text models
     */
    private toTextModelDecorators(
        valueHelpers: ValueHelperState[],
        labelField: 'descText' | 'codeLevel' = 'descText',
    ): Array<DisplayDecorator<TextModel>> {
        return valueHelpers.map(
            (vh) => new DisplayDecorator<TextModel>(vh[labelField], new TextModel(vh[labelField], vh.code)),
        );
    }

    public getMaterialMK(): Array<DisplayDecorator<TextModel>> {
        this.checkIfServiceIsInitialized(`getMaterialMK`);
        return this.toTextModelDecorators(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDMaterialMK))(),
        );
    }

    public getTimeRecordingBillableCodes(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getTimeRecordingBillableCodes`);
        this.warnIfConfigNotLoaded(`getTimeRecordingBillableCodes`);
        return this.toValueHelperModels(this.store.selectSignal(selectTimeRecordingBillableCodes)());
    }

    public getTimeRecordingTaskTypeInfo(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getTimeRecordingTaskTypeInfo`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDTimeRecordingTaskTypeInfo))(),
        );
    }

    public getRemoteWorkTimeRecordingTaskTypeInfo(status: StatusModel): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getRemoteWorkTimeRecordingTaskTypeInfo`);
        return this.toValueHelperModels(
            this.store.selectSignal(
                selectRemoteWorkTimeRecordingTaskTypeInfo(
                    status.isRemoteWork(),
                    status.isRemoteWorkOrTraining(),
                    status.isRemoteSupport(),
                ),
            )(),
        );
    }

    public getPhoneFixIndicator(): boolean {
        this.checkIfServiceIsInitialized(`getPhoneFixIndicator`);
        return this.store.selectSignal(selectPhoneFixIndicator)();
    }

    public getMeansOfTransportation(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getMeansOfTransportation`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDMeansOfTransportation))(),
        );
    }

    public getTravelDestination(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getTravelDestination`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDTravelDestination))(),
        );
    }

    public getTimesheetGapTypeInfo(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getTimesheetGapTypeInfo`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDTimesheetGapTypeInfo))(),
        );
    }

    public getCreateMatOrderPriority(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getCreateMatOrderPriority`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDCreateMatOrderPriority))(),
        );
    }

    public getFollowUpUMRCode(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getFollowUpUMRCode`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDFollowUpUMRCode))(),
        );
    }

    public getBillable(): Array<DisplayDecorator<TextModel>> {
        this.checkIfServiceIsInitialized(`getBillable`);
        this.warnIfConfigNotLoaded(`getBillable`);
        return this.toTextModelDecorators(this.store.selectSignal(selectBillableCodes)());
    }

    public getMaterialErrorTubeJ(): Array<DisplayDecorator<TextModel>> {
        this.checkIfServiceIsInitialized(`getMaterialErrorTubeJ`);
        return this.toTextModelDecorators(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDMaterialErrorTubeJ))(),
        );
    }

    public getMaterialErrorTubeI(): Array<DisplayDecorator<TextModel>> {
        this.checkIfServiceIsInitialized(`getMaterialErrorTubeI`);
        return this.toTextModelDecorators(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDMaterialErrorTubeI))(),
        );
    }

    public getMaterialBug(): Array<DisplayDecorator<TextModel>> {
        this.checkIfServiceIsInitialized(`getMaterialBug`);
        return this.toTextModelDecorators(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDMaterialBug))(),
        );
    }

    public getReturnType(): Array<DisplayDecorator<TextModel>> {
        this.checkIfServiceIsInitialized(`getReturnType`);
        return this.toTextModelDecorators(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDReturnType))(),
        );
    }

    public getMaterialReturnCause(): Array<DisplayDecorator<TextModel>> {
        this.checkIfServiceIsInitialized(`getMaterialReturnCause`);
        return this.toTextModelDecorators(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDMaterialReturnCause))(),
        );
    }

    public getMaterialPhiDataCode(): Array<DisplayDecorator<TextModel>> {
        this.checkIfServiceIsInitialized(`getMaterialPhiDataCode`);
        return this.toTextModelDecorators(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDMaterialPhiDataCode))(),
            'codeLevel',
        );
    }

    public getQuoteType(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getQuoteType`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDQuoteType))(),
        );
    }

    public getComplaintQuestions(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getComplaintQuestions`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDComplaintQuestions))(),
        );
    }

    public getFollowDispatchTime(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getFollowDispatchTime`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDFollowDispatchTime))(),
        );
    }

    public getQualityControlQuestions(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getQualityControlQuestions`);
        return this.toValueHelperModels(this.store.selectSignal(selectQualityControlQuestions)());
    }

    public getTimesheetActivityTypeInfo(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getTimesheetActivityTypeInfo`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDTimesheetActivityType))(),
        );
    }

    public getPdaConfigValues(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getPdaConfigValues`);
        return this.toValueHelperModels(this.store.selectSignal(selectPdaConfigValues)());
    }

    public getOsrLimitValueHelper(): ValueHelperModel | undefined {
        this.checkIfServiceIsInitialized(`getOsrLimitValueHelper`);
        const valueHelper = this.store.selectSignal(selectOsrLimitValueHelper)();
        return valueHelper ? ValueHelperModel.fromState(valueHelper) : undefined;
    }

    public getAttendeeTitles(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getAttendeeTitles`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDAttendeeTitles))(),
        );
    }

    public getParticipantTitles(): FormParticipantTitles {
        return this.store.selectSignal(selectParticipantTitles)();
    }

    public getModalityValue(modality: string): ValueHelperModel | undefined {
        this.checkIfServiceIsInitialized(`getModalityValue`);
        const valueHelper = this.store.selectSignal(selectModalityValue(modality))();
        return valueHelper ? ValueHelperModel.fromState(valueHelper) : undefined;
    }

    public getFollowupReason(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getFollowupReason`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDFollowupReason))(),
        );
    }

    public getSRSVPNConnection(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getSRSVPNConnection`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDSRSVPNConnection))(),
        );
    }

    public getAddMaterialTypes(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getAddMaterialTypes`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDAddMaterialType))(),
        );
    }

    public getDangerousGoodWarning(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getDangerousGoodWarning`);
        return this.toValueHelperModels(this.store.selectSignal(selectDangerousGoodWarning)());
    }

    public getFurtherLinks(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getFurtherLinks`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDFurtherLinks))(),
        );
    }

    public getTravelOfficeStart(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getTravelOfficeStart`);
        return this.toValueHelperModels(this.store.selectSignal(selectTravelOfficeStart)());
    }

    public getCustomerSignatureAgreementTextClause(): string {
        this.checkIfServiceIsInitialized(`getCustomerSignatureAgreementTextClause`);
        return this.store.selectSignal(selectCustomerSignatureAgreementTextClause)();
    }

    public getUploadToDocboxValueHelpers(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getUploadToDocboxValueHelpers`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDUploadToDocbox))(),
        );
    }

    public getMaterialConsumptionFieldsLabels(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getMaterialConsumptionFieldsLabels`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDMaterialConsumedLabel))(),
        );
    }

    public getStatesOfUSA(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getStatesOfUSA`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDStatesOfUSA))(),
        );
    }

    public getFDAFormIntendedUses(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getFDAFormIntendedUses`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDFDAFormIntendedUses))(),
        );
    }

    public getXRaySystems(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getXRaySystems`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperXraySystems))(),
        );
    }

    public getFDAFormCertifiedComponent(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getFDAFormCertifiedComponent`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDFDAFormCertifiedComponent))(),
        );
    }

    public getFDAFormOtherCertifiedComponent(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getFDAFormOtherCertifiedComponent`);
        return this.toValueHelperModels(
            this.store.selectSignal(
                selectValueHelpersById(ConfigConstants.valueHelperIDFDAFormOtherCertifiedComponent),
            )(),
        );
    }

    public getFDAFormComponentInfo(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getFDAFormComponentInfo`);
        return this.toValueHelperModels(
            this.store.selectSignal(selectValueHelpersById(ConfigConstants.valueHelperIDFDAFormComponentInfo))(),
        );
    }
}
